import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";

export const ImageInputField = forwardRef(
  (
    {
      id,
      imageUrl,
      onFileChange,
      required = false,
      showError = true,
      acceptedFormats = "image/*",
      maxFileSize = 5 * 1024 * 1024,
      label,
    },
    ref
  ) => {
    const [error, setError] = useState("");
    const [hasInteracted, setHasInteracted] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    // ✅ Add ref to access the actual input element
    const inputRef = useRef(null);

    const validateFile = useCallback(
      (file) => {
        if (!file && required) {
          return "Image is required";
        }

        if (!file) {
          return "";
        }

        // Check file type
        if (!file.type.startsWith("image/")) {
          return "Please select a valid image file";
        }

        // More specific image format validation
        if (acceptedFormats !== "image/*") {
          const allowedTypes = acceptedFormats
            .split(",")
            .map((type) => type.trim().toLowerCase());
          if (!allowedTypes.includes(file.type.toLowerCase())) {
            const formatNames = allowedTypes
              .map((type) => type.replace("image/", "").toUpperCase())
              .join(", ");
            return `Please select a valid image format: ${formatNames}`;
          }
        }

        // Check file size
        if (maxFileSize && file.size > maxFileSize) {
          const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
          return `File size must be less than ${maxSizeMB}MB`;
        }

        return "";
      },
      [required, maxFileSize, acceptedFormats]
    );

    // ✅ Enhanced reset function
    useImperativeHandle(
      ref,
      () => ({
        validate: () => {
          const validationError = validateFile(selectedFile);
          setError(validationError);
          return validationError;
        },
        touch: () => {
          setHasInteracted(true);
        },
        getValue: () => selectedFile,
        getError: () => error,
        isRequired: () => required,
        reset: () => {
          // ✅ Clear the actual HTML input value
          if (inputRef.current) {
            inputRef.current.value = "";
          }

          setSelectedFile(null);
          setError("");
          setHasInteracted(false);

          // Clean up preview URL on reset
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        },
      }),
      [validateFile, selectedFile, error, required, previewUrl]
    );

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      const validationError = validateFile(file);

      if (!hasInteracted) {
        setHasInteracted(true);
      }

      setSelectedFile(file);
      setError(validationError);

      // Create preview URL for the selected file
      if (file) {
        // Cleanup previous preview URL
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        const newPreviewUrl = URL.createObjectURL(file);
        setPreviewUrl(newPreviewUrl);
      } else {
        // Clear preview if no file selected
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
      }

      // Always call parent onChange so parent can track file selection
      if (onFileChange && typeof onFileChange === "function") {
        onFileChange(e);
      }
    };

    // ✅ Enhanced click handler to clear input value
    const handleClick = () => {
      // Clear the input value before opening file dialog
      // This ensures onChange fires even for the same file
      if (inputRef.current) {
        inputRef.current.value = "";
      }

      // Mark as interacted when user clicks to select file
      if (!hasInteracted) {
        setHasInteracted(true);
      }
    };

    const handleBlur = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
      }
      // Re-validate on blur to show required errors
      const validationError = validateFile(selectedFile);
      setError(validationError);
    };

    // Cleanup preview URL on component unmount or file change
    useEffect(() => {
      return () => {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      };
    }, [previewUrl]);

    // Initial validation for required field when component mounts
    useEffect(() => {
      if (hasInteracted) {
        const validationError = validateFile(selectedFile);
        setError(validationError);
      }
    }, [selectedFile, validateFile, hasInteracted]);

    // Determine which image to show: preview, existing URL, or fallback
    const displayImageUrl = previewUrl || imageUrl;
    const fallbackImageUrl =
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f8f9fa'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='.3em' fill='%236c757d' font-size='12'%3ENo Image%3C/text%3E%3C/svg%3E";

    return (
      <div className="text-center">
        {label && (
          <label className="form-label d-block mb-2">
            {label} {required ? <span className="required-field">*</span> : ""}
          </label>
        )}

        <div className="profile-user image-holder position-relative d-inline-block mx-auto mb-4">
          <img
            src={displayImageUrl || fallbackImageUrl}
            className={`rounded-circle avatar-xl img-thumbnail user-profile-image shadow ${
              error && hasInteracted ? "border-danger" : ""
            }`}
            alt="user-profile"
            onError={(e) => {
              // Fallback image if the URL is broken
              e.target.src = fallbackImageUrl;
            }}
            style={{
              width: "100px",
              height: "100px",
              objectFit: "cover",
            }}
          />

          <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
            <input
              ref={inputRef} // ✅ Add ref to input
              id={id}
              type="file"
              className={`profile-img-file-input ${
                required ? "required" : ""
              } ${error && hasInteracted ? "is-invalid" : ""}`}
              onChange={handleFileChange}
              onClick={handleClick}
              onBlur={handleBlur}
              accept={acceptedFormats}
              style={{ display: "none" }}
              aria-describedby={
                error && hasInteracted ? `${id}-error` : undefined
              }
            />
            <label
              htmlFor={id}
              className={`profile-photo-edit avatar-xs ${
                error && hasInteracted ? "border-danger" : ""
              }`}
              style={{ cursor: "pointer" }}
              title={selectedFile ? "Change image" : "Select image"}
            >
              <span className="avatar-title rounded-circle bg-light text-body shadow">
                <i className="ri-camera-fill"></i>
              </span>
            </label>
          </div>
        </div>

        {/* File info display */}
        {selectedFile && !error && (
          <div className="text-muted small mb-2">
            <div>{selectedFile.name}</div>
            <div>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
          </div>
        )}

        {/* Error message */}
        {showError && error && hasInteracted && (
          <div
            className="text-danger small mt-1"
            id={`${id}-error`}
            role="alert"
          >
            {error}
          </div>
        )}

        {/* Helper text */}
        {!error && !selectedFile && maxFileSize && (
          <div className="text-muted small mt-1">
            Max file size: {Math.round(maxFileSize / (1024 * 1024))}MB
            {acceptedFormats !== "image/*" && (
              <span>
                {" • Formats: "}
                {acceptedFormats
                  .split(",")
                  .map((type) => type.replace("image/", "").toUpperCase())
                  .join(", ")}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

// ✅ Add display name for better debugging
ImageInputField.displayName = "ImageInputField";
